// src/features/reservations/lib/reservationFormat.ts
//
// Shared formatting for the `ReservationState` sub-objects (preCheckIn,
// payment, key, identification) — used by Llegadas de hoy, Reservas and the
// reservation detail so the three screens compose the exact same detail
// strings from the same facts (proposal §3/§6.1: "Hecho · hace 3 d", "A
// medias · Identificación", "2/2", …). No raw event codes, no provider names.
import { format, formatDistanceToNowStrict, type Locale } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import type { TranslateFunction } from 'react-admin';
import { getSectionLabel } from '@/design/registry/sectionRegistry';
import { DASH, formatMoney } from '@/lib/analyticsFormat';
import { humanizeKey } from '@/lib/v2Vocabulary';
import type {
  KeyFacts,
  PaymentFacts,
  PreCheckInFacts,
  ReservationState,
} from '@/types/reservationState';

export const relativeTime = (iso: string | null | undefined, locale: string): string => {
  if (!iso) return '';
  try {
    return formatDistanceToNowStrict(new Date(iso), { locale: locale === 'en' ? enUS : es, addSuffix: false });
  } catch {
    return '';
  }
};

const isSameDay = (iso: string): boolean => {
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};

const sectionLabel = (code: string | null | undefined, translate: TranslateFunction): string =>
  getSectionLabel(code, translate);

/** «Hecho · hace 3 d» / «Hecho · hoy» / «A medias · Identificación» / «—». */
export const formatPreCheckIn = (
  preCheckIn: PreCheckInFacts,
  translate: TranslateFunction,
  locale: string
): string => {
  if (preCheckIn.state === 'done' && preCheckIn.doneAt) {
    if (isSameDay(preCheckIn.doneAt)) return translate('resources.arrivals.preCheckIn.doneToday');
    return translate('resources.arrivals.preCheckIn.doneAgo', { time: relativeTime(preCheckIn.doneAt, locale) });
  }
  if (preCheckIn.state === 'started') {
    return translate('resources.arrivals.preCheckIn.partialAt', {
      section: sectionLabel(preCheckIn.lastSection, translate),
    });
  }
  return translate('resources.arrivals.preCheckIn.none');
};

/** «Pagado» / «Pendiente» / «Fallido» / «—». */
export const formatPayment = (payment: PaymentFacts, translate: TranslateFunction): string => {
  switch (payment.state) {
    case 'paid':
      return translate('resources.arrivals.payment.paid');
    case 'pending':
      return translate('resources.arrivals.payment.pending');
    case 'failed':
      return translate('resources.arrivals.payment.failed');
    default:
      return translate('resources.arrivals.payment.none');
  }
};

/** «253,84 €» — the amount charged, in the reservation's currency. */
export const formatPaymentAmount = (payment: Pick<PaymentFacts, 'amount' | 'currency'>): string =>
  payment.amount === null || payment.amount === undefined ? DASH : formatMoney(payment.amount, payment.currency || 'EUR');

/**
 * The row's «Cobrado» (P03): charges minus refunds of the reservation, one amount per
 * currency. «—» when it cannot be measured (paid without transaction detail) or when
 * nothing was charged; never the last charge as an estimate.
 */
export const formatRowCharged = (payment: Pick<PaymentFacts, 'charged' | 'chargedMeasurable'>): string =>
  !payment.chargedMeasurable || !payment.charged || payment.charged.length === 0
    ? DASH
    : payment.charged.map((c) => formatMoney(c.amount, c.currency)).join(' · ');

/** CSV cells for the row's «Cobrado»: a number and its currency with one currency, both joined with several, empty without a figure. */
export const rowChargedCsv = (payment: Pick<PaymentFacts, 'charged' | 'chargedMeasurable'>): { amount: number | string | null; currency: string } => {
  if (!payment.chargedMeasurable || !payment.charged || payment.charged.length === 0) return { amount: null, currency: '' };
  if (payment.charged.length === 1) return { amount: payment.charged[0].amount, currency: payment.charged[0].currency };
  return { amount: payment.charged.map((c) => c.amount).join(' / '), currency: payment.charged.map((c) => c.currency).join(' / ') };
};

/** «tarjeta en terminal» — a payment method as it reads inside a sentence. */
export const paymentMethodLabel = (method: string | null | undefined, translate: TranslateFunction): string => {
  if (!method) return translate('resources.payments.byMethod.methods.unknown').toLowerCase();
  return translate(`resources.payments.byMethod.methods.${method}`, { _: humanizeKey(method) }).toLowerCase();
};

/** «Tarjeta» / «Llave digital» for a key type stated as a bare word. */
export const keyTypeLabel = (keyType: string | null | undefined, translate: TranslateFunction): string =>
  keyType ? translate(`resources.arrivals.key.${keyType}`, { _: humanizeKey(keyType) }) : DASH;

/** «n/m» identified, or «—» when unknown. */
export const formatIdentification = (identified: number | null, total: number | null): string => {
  if (identified === null || total === null) return '—';
  return `${identified}/${total}`;
};

/** «Tarjeta» / «Llave digital» / «—». */
export const formatKey = (key: KeyFacts, translate: TranslateFunction): string => {
  if (key.type === 'card') return translate('resources.arrivals.key.card');
  if (key.type === 'digital') return translate('resources.arrivals.key.digital');
  return translate('resources.arrivals.key.none');
};

/** Guest display name in clear; the reservation number when unknown (never masked here — the API sends the clear name or null per retention). */
/** Time-of-day evidence available on a `ReservationState`, best-effort (the
 *  contract carries no scheduled arrival time — see Decision-14 note in
 *  reports/wave2-ui.md): check-in time, else the live section's start time. */
export const arrivalTime = (row: Pick<ReservationState, 'checkIn' | 'live'>, locale: Locale): string => {
  const iso = row.checkIn.completedAt || row.live.sectionEnteredAt;
  if (!iso) return DASH;
  try {
    return format(new Date(iso), 'HH:mm', { locale });
  } catch {
    return DASH;
  }
};

export const displayIdentifier = (row: Pick<ReservationState, 'guestName' | 'reservationNumber'>): string =>
  row.guestName || row.reservationNumber;

/** «Lleva 6 min en Pago» — the one amber accent per view. */
export const formatStuckFlag = (
  minutesInSection: number | null,
  section: string | null,
  translate: TranslateFunction
): string =>
  translate('resources.arrivals.stuckFlag', {
    minutes: minutesInSection ?? 0,
    section: sectionLabel(section, translate),
  });
